import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Auth0Provider, useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { type AuthUser, AuthUserProvider } from "@package/react/auth";
import { logger } from "@artis/logger";
import posthog from "posthog-js";

import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from "../../globals";
import { captureEvent } from "../../utils/o11y";
import { BlockedUserMessage } from "./BlockedUserMessage";
import { RestrictedAccessMessage } from "../../webapp/components/RestrictedAccessMessage";
import { hideInitialLoader } from "../../webapp/components/initial-loader";
import {
  AUTH_CALLBACK_FAILED,
  AUTH_CALLBACK_RETRY_KEY,
  AUTH_ERROR_KEY,
  AUTH_REDIRECT_COUNT_KEY,
  FORCE_LOGIN_PARAM,
  LOGGED_OUT_PARAM,
  MAX_AUTH_REDIRECTS,
} from "./constants";
import {
  getAuthErrorCode,
  isAuthCallbackFailure,
  resolveAuthCallbackFailureAction,
  stripAuthCallbackSearchParams,
} from "./authCallbackFailure";
import { registerAuthCodeCallback } from "./authRedirectLoop";
import { getIsLoggingOut, setLoggingOut } from "./logoutState";
import { postLoginReturnUrl, replaceUrlPreservingHistoryState } from "./postLoginReturn";
import { useProductConfigsCache } from "../../data/cached/preload";
import "@artis/ui/tokens.css";
import { useAccessToken, useTokenRefreshWatchdog } from "./hooks";

// Broadcast channel for logout events
const LogoutBroadcastChannel = new BroadcastChannel("logout");
const LogoutContext = createContext(() => Promise.resolve());
export const LogoutProvider = LogoutContext.Provider;
export const useLogout = () => useContext(LogoutContext);

const forceLogin = new URLSearchParams(window.location.search).get(FORCE_LOGIN_PARAM) === "1";
if (forceLogin) {
  // Strip the flag from the address bar; loginOptions below still see it via `forceLogin`.
  const clean = new URL(window.location.href);
  clean.searchParams.delete(FORCE_LOGIN_PARAM);
  window.history.replaceState({}, document.title, clean.pathname + clean.search + clean.hash);
}

// Auth0 error codes that mean we cannot recover silently: the user must re-authenticate.
// Source: @auth0/auth0-spa-js Auth0Client.ts and Auth0 docs on Failed Silent Auth.
const UNRECOVERABLE_AUTH_CODES = new Set([
  "login_required",
  "invalid_grant",
  "missing_refresh_token",
  "consent_required",
]);

export const isAuthUnrecoverable = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const code = (err as { error?: unknown }).error;
  return typeof code === "string" && UNRECOVERABLE_AUTH_CODES.has(code);
};

const persistAuthCallbackFailure = (err: unknown) => {
  const errorCode = getAuthErrorCode(err) ?? "unknown";
  const description = err instanceof Error ? err.message : "Authentication callback failed";
  sessionStorage.setItem(
    AUTH_ERROR_KEY,
    JSON.stringify({
      errorParam: AUTH_CALLBACK_FAILED,
      errorDescription: description,
      errorCode,
    }),
  );
  const { search } = stripAuthCallbackSearchParams(window.location.search);
  window.history.replaceState({}, document.title, window.location.pathname + search + window.location.hash);
  hideInitialLoader();
};

function checkForAuthError(): boolean {
  // Check if we already detected an error (persisted across reloads)
  if (sessionStorage.getItem(AUTH_ERROR_KEY)) {
    hideInitialLoader();
    return true;
  }

  // Check URL params BEFORE Auth0 SDK processes them
  const params = new URLSearchParams(window.location.search);
  const errorParam = params.get("error");
  const errorDescription = params.get("error_description");

  if (errorParam) {
    logger.warn("AuthProvider: Auth0 returned an error in the callback URL", {
      tags: ["auth"],
      error_param: errorParam,
      error_description: errorDescription ?? undefined,
    });
    sessionStorage.setItem(AUTH_ERROR_KEY, JSON.stringify({ errorParam, errorDescription }));
    window.history.replaceState({}, document.title, window.location.pathname);
    hideInitialLoader();
    return true;
  }

  // Detect login loop: if we see ?code= in the URL, it means Auth0 redirected back
  // Count these redirects - if too many in one session, something is wrong
  const currentCount = Number(sessionStorage.getItem(AUTH_REDIRECT_COUNT_KEY) || "0");
  const { counted, nextCount, isLoginLoop } = registerAuthCodeCallback({
    search: window.location.search,
    currentCount,
    maxRedirects: MAX_AUTH_REDIRECTS,
  });
  if (counted) {
    sessionStorage.setItem(AUTH_REDIRECT_COUNT_KEY, String(nextCount));
    // Debug rather than warn: a healthy login lands here exactly once.
    logger.debug("AuthProvider: Auth0 callback landing", { tags: ["auth"], redirect_attempt: nextCount });

    if (isLoginLoop) {
      logger.warn("AuthProvider: login loop detected, showing the error screen", {
        tags: ["auth"],
        redirect_attempt: nextCount,
        max_redirects: MAX_AUTH_REDIRECTS,
      });
      sessionStorage.setItem(
        AUTH_ERROR_KEY,
        JSON.stringify({
          errorParam: "login_loop",
          errorDescription: `Authentication failed after ${nextCount} attempts. This may indicate an issue with your account.`,
        }),
      );
      window.history.replaceState({}, document.title, window.location.pathname);
      hideInitialLoader();
      return true;
    }
  }

  return false;
}

// Run IMMEDIATELY at module level, before any React rendering
const hasAuthError = checkForAuthError();
const isLoggedOutLanding = new URLSearchParams(window.location.search).get(LOGGED_OUT_PARAM) === "1";
if (isLoggedOutLanding) {
  hideInitialLoader();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // If there's an auth error, show the message immediately without loading Auth0
  if (hasAuthError) {
    return <BlockedUserMessage />;
  }

  if (isLoggedOutLanding) {
    return (
      <div data-curve-mgmt-tokens className="w-full h-dvh flex flex-col">
        <RestrictedAccessMessage
          title="You've been logged out"
          message="You logged out of your account in another tab or window."
          iconColorClassName="text-[#FFC942]"
          messageClassName="text-base font-normal leading-5 text-center text-[var(--colour-text-light)]"
          actions={[
            {
              label: "Log In",
              variant: "primary",
              onClick: () => {
                const url = new URL(window.location.href);
                url.searchParams.delete(LOGGED_OUT_PARAM);
                window.location.href = url.toString();
              },
            },
          ]}
        />
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      useRefreshTokensFallback={true}
      onRedirectCallback={(state) => {
        captureEvent("login", {
          $set_once: { first_login: new Date().toISOString() },
        });
        // The router mounts after this callback and reads the address bar.
        // replaceState opens the return path in this document. Assigning
        // location.href downloads and boots the app a second time.
        // Android Chrome can still be on a desktop-width viewport in this document.
        // index.html reloads that return once before the module boots.
        const url = postLoginReturnUrl(state?.returnTo, window.location);
        if (url) replaceUrlPreservingHistoryState(url, window.history, document.title);
      }}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: "hasura",
      }}
    >
      <ProvideLogout>
        <AuthCallbackGuard>
          <AuthRedirect>
            <ProvideAuthUser>{children}</ProvideAuthUser>
          </AuthRedirect>
        </AuthCallbackGuard>
      </ProvideLogout>
    </Auth0Provider>
  );
}

/**
 * Handles failed Auth0 redirect callbacks (missing/overwritten PKCE transaction).
 *
 * First failure: strip the dead ?code= URL and start one fresh loginWithRedirect.
 * Auth0 SSO usually completes this silently so the user never sees an error.
 *
 * Second failure: stop and show BlockedUserMessage (avoids a redirect loop).
 */
export const AuthCallbackGuard = ({ children }: { children: ReactNode }) => {
  const { isLoading, error, loginWithRedirect } = useAuth0();
  const [fatalCallbackFailure, setFatalCallbackFailure] = useState(() =>
    Boolean(sessionStorage.getItem(AUTH_ERROR_KEY)),
  );
  const retryStartedRef = useRef(false);

  const action = resolveAuthCallbackFailureAction({
    isLoading,
    error,
    alreadyRetried: sessionStorage.getItem(AUTH_CALLBACK_RETRY_KEY) === "1" || fatalCallbackFailure,
    hasPersistedAuthError: Boolean(sessionStorage.getItem(AUTH_ERROR_KEY)) || fatalCallbackFailure,
  });

  useEffect(() => {
    if (action !== "retry" && action !== "fatal") return;
    if (!isAuthCallbackFailure(error)) return;

    // Dead callback URL must go before we start a new authorize, or Auth0 / our
    // redirect counter will keep treating the same ?code= as a new attempt.
    const { search } = stripAuthCallbackSearchParams(window.location.search);
    window.history.replaceState({}, document.title, window.location.pathname + search + window.location.hash);

    if (action === "retry") {
      if (retryStartedRef.current) return;
      retryStartedRef.current = true;
      sessionStorage.setItem(AUTH_CALLBACK_RETRY_KEY, "1");
      logger.warn("AuthProvider: Auth0 callback failed, starting one automatic re-login", {
        tags: ["auth"],
        error_code: getAuthErrorCode(error) ?? "unknown",
        message: error instanceof Error ? error.message : undefined,
      });
      void loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }

    if (!sessionStorage.getItem(AUTH_ERROR_KEY)) {
      persistAuthCallbackFailure(error);
    }
    logger.warn("AuthProvider: Auth0 callback failed again after re-login, showing error", {
      tags: ["auth"],
      error_code: getAuthErrorCode(error) ?? "unknown",
      message: error instanceof Error ? error.message : undefined,
    });
    setFatalCallbackFailure(true);
  }, [action, error, loginWithRedirect]);

  // Layout effect, not render: the loader is a full-screen overlay, so it has to
  // be gone before the error screen is painted underneath it.
  useLayoutEffect(() => {
    if (action === "fatal") hideInitialLoader();
  }, [action]);

  if (action === "wait" || action === "retry") {
    return null;
  }

  if (action === "fatal") {
    return <BlockedUserMessage reason={AUTH_CALLBACK_FAILED} />;
  }

  return children;
};

const AuthRedirect = withAuthenticationRequired(Fragment, {
  loginOptions: {
    appState: { returnTo: window.location.pathname },
    // After "Return to Log In" we cleared the Auth0 session; still ask Auth0 to
    // show the credential form so SSO / IdP cookies can't silently sign the user in.
    ...(forceLogin ? { authorizationParams: { prompt: "login" } } : {}),
  },
  onBeforeAuthentication: () => {
    if (getIsLoggingOut()) return Promise.reject(new Error("logout_in_progress"));
    // Defense in depth: never start a new login while a prior auth error is latched.
    if (sessionStorage.getItem(AUTH_ERROR_KEY)) {
      return Promise.reject(new Error("auth_error_present"));
    }
    return Promise.resolve();
  },
});

const ProvideAuthUser = ({ children }: { children: React.ReactNode }) => {
  // Single token refresh watchdog: drives a 5-minute interval that refetches
  // the shared access-token query, which decodes the cached token and forces
  // a hard refresh from Auth0 when within HARD_REFRESH_WINDOW_SEC of expiry.
  useTokenRefreshWatchdog();

  const token = useAccessToken();
  const { user, loginWithRedirect } = useAuth0();
  const [isBlocked, setIsBlocked] = useState(false);
  const hasInitiatedReloginRef = useRef(false);

  // IMPORTANT: All hooks must be called before any conditional returns
  const authUser = useMemo<AuthUser | null>(() => {
    if (!user?.sub || !token.data?.access_token) return null;
    return {
      id: user.sub,
      email: user.email,
      username: user.username,
      token: token.data?.access_token,
    };
  }, [user?.sub, user?.email, user?.username, token.data?.access_token]);

  // Check if token fetch failed due to blocked user
  useEffect(() => {
    if (token.error) {
      const errorMessage = token.error.message?.toLowerCase() || "";
      const code = (token.error as { error?: unknown }).error;
      logger.info("AuthProvider: token fetch error detected", {
        tags: ["auth"],
        error_code: typeof code === "string" ? code : "unknown",
        message: errorMessage,
      });

      // Be specific: only catch explicit blocked errors
      if (
        errorMessage.includes("user is blocked") ||
        errorMessage.includes("account has been blocked") ||
        errorMessage.includes("user_blocked")
      ) {
        logger.info("AuthProvider: user blocked during token fetch, showing blocked message", { tags: ["auth"] });
        setIsBlocked(true);
      }
    }
  }, [token.error]);

  // Handle unrecoverable silent-auth failures (login_required, invalid_grant, etc.)
  // by triggering an interactive re-authentication. The login-loop guard in
  // checkForAuthError prevents infinite redirect cycles.
  useEffect(() => {
    if (!token.error || !isAuthUnrecoverable(token.error)) return;
    if (hasInitiatedReloginRef.current) return;
    if (getIsLoggingOut()) return;
    hasInitiatedReloginRef.current = true;
    const code = (token.error as { error?: unknown }).error;
    logger.info("AuthProvider: unrecoverable silent-auth error, redirecting to login", {
      tags: ["auth"],
      error_code: typeof code === "string" ? code : "unknown",
    });
    loginWithRedirect({ appState: { returnTo: window.location.pathname } });
  }, [token.error, loginWithRedirect]);

  // Clear the login-loop redirect counter only once the full auth flow has
  // succeeded: OAuth code exchanged AND access token confirmed. Clearing it
  // earlier (e.g. in onRedirectCallback) means the counter resets to 0 on
  // every code exchange, so a loop of "successful code exchange → failed
  // silent auth → loginWithRedirect" can never accumulate enough to surface
  // the BlockedUserMessage.
  useEffect(() => {
    if (authUser) {
      sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);
      sessionStorage.removeItem(AUTH_CALLBACK_RETRY_KEY);
    }
  }, [authUser]);

  // Conditional returns AFTER all hooks
  if (isBlocked) {
    return <BlockedUserMessage />;
  }

  // Don't render children until the access token is fetched and authUser is populated.
  // Children like RefreshAccessToken and BlockedUserGuard call useAuthUser(), which throws
  // when the context value is null. There is a brief window between Auth0 completing
  // authentication and the access token query resolving where authUser is still null.
  if (!authUser) {
    return null;
  }

  return <AuthUserProvider value={authUser}>{children}</AuthUserProvider>;
};

const ProvideLogout = ({ children }: { children: React.ReactNode }) => {
  const { logout } = useAuth0();
  const cache = useProductConfigsCache();

  const onLogout = useCallback((): Promise<void> => {
    logger.info("[logout] start", { tags: ["auth"] });
    setLoggingOut();

    const isOnLiveCharts = window.location.pathname.includes("/trading-chart");

    // Broadcast FIRST so followers navigate away before shared storage is wiped.
    LogoutBroadcastChannel.postMessage("logout");
    logger.info("[logout] broadcast sent", { tags: ["auth"] });

    // 1. Track logout in PostHog
    captureEvent("user_logout");
    posthog.reset();

    // 2. Clear synchronous storages
    sessionStorage.clear();
    localStorage.clear();
    logger.info("[logout] sync storages cleared", { tags: ["auth"] });
    cache.clear().catch(console.error);

    // 3. Clear cookies (synchronous)
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      // biome-ignore lint/suspicious/noDocumentCookie: This is intentional.
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
    logger.info("[logout] cookies cleared", { tags: ["auth"] });

    if ("caches" in window) {
      caches
        .keys()
        .then((names) => Promise.all(names.map((n) => caches.delete(n))))
        .catch(console.error);
    }
    if (window.indexedDB && indexedDB.databases) {
      indexedDB
        .databases()
        .then((dbs) => {
          for (const db of dbs) {
            if (db.name) indexedDB.deleteDatabase(db.name);
          }
        })
        .catch(console.error);
    }
    if (navigator.serviceWorker) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(console.error);
    }

    const returnToPath = isOnLiveCharts ? "/app/trading-chart" : "";
    const returnTo = `${window.location.origin}${returnToPath}`;

    logger.info("[logout] calling Auth0 logout()", { tags: ["auth"], returnTo });
    return logout({
      logoutParams: {
        returnTo,
        client_id: AUTH0_CLIENT_ID,
      },
    });
  }, [logout, cache]);

  const onRemoteLogout = useCallback(() => {
    const isOnLiveCharts = window.location.pathname.includes("/trading-chart");
    const returnToPath = isOnLiveCharts ? "/app/trading-chart" : "";
    const url = new URL(`${window.location.origin}${returnToPath}`);
    url.searchParams.set(LOGGED_OUT_PARAM, "1");
    window.location.href = url.toString();
  }, []);

  useEffect(() => {
    // Listen for "logout" messages from other tabs and call our onLogout
    LogoutBroadcastChannel.addEventListener("message", onRemoteLogout);
    return () => {
      LogoutBroadcastChannel.removeEventListener("message", onRemoteLogout);
    };
  }, [onRemoteLogout]);

  return <LogoutContext.Provider value={onLogout}>{children}</LogoutContext.Provider>;
};
